// Allows Unicode letters (incl. Hebrew, Arabic), spaces, hyphens, apostrophes
const NAME_RE = /^[\p{L}\s'-]+$/u;
// Allows digits, spaces, dashes, plus sign
const PHONE_RE = /^[\d\s\-+]+$/;
// Standard email format
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface CustomerFormErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  email?: string;
}

export interface CustomerFormFields {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  email: string; // optional field; empty string when not set
  registrationDate: string; // ISO "YYYY-MM-DD" or '' when not set
}

type TFunction = (_key: string, _options?: Record<string, string | number>) => string;

function validateName(value: string, fieldKey: 'firstName' | 'lastName', t: TFunction): string | undefined {
  const v = value.trim();
  const label = t(`fields.${fieldKey}`);
  if (!v) return t('validation.required', { label });
  if (v.length < 2) return t('validation.tooShort', { label, min: 2 });
  if (v.length > 50) return t('validation.tooLong', { label, max: 50 });
  if (!NAME_RE.test(v)) return t('validation.invalidChars', { label });
}

function validatePhone(value: string, t: TFunction): string | undefined {
  const v = value.trim();
  if (!v) return t('validation.phoneRequired');
  if (!PHONE_RE.test(v)) return t('validation.phoneInvalidChars');
  const digits = v.replace(/\D/g, '');
  if (digits.length < 7) return t('validation.phoneTooShort');
  if (digits.length > 15) return t('validation.phoneTooLong');
}

function validateAddress(value: string, t: TFunction): string | undefined {
  const v = value.trim();
  if (!v) return t('validation.required', { label: t('fields.address') });
  if (v.length > 200) return t('validation.addressTooLong');
}

function validateEmail(value: string, t: TFunction): string | undefined {
  const v = value.trim();
  if (!v) return undefined; // optional field
  if (!EMAIL_RE.test(v)) return t('validation.emailInvalid');
}

export function validateCustomerForm(fields: CustomerFormFields, t: TFunction): CustomerFormErrors {
  const errors: CustomerFormErrors = {};
  const firstName = validateName(fields.firstName, 'firstName', t);
  const lastName = validateName(fields.lastName, 'lastName', t);
  const phone = validatePhone(fields.phone, t);
  const address = validateAddress(fields.address, t);
  const email = validateEmail(fields.email, t);
  if (firstName) errors.firstName = firstName;
  if (lastName) errors.lastName = lastName;
  if (phone) errors.phone = phone;
  if (address) errors.address = address;
  if (email) errors.email = email;
  return errors;
}
